import { PushNotifications } from '@capacitor/push-notifications';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db, messaging } from '../firebaseConfig';
import { Capacitor } from '@capacitor/core';
import { getToken, onMessage } from "firebase/messaging";
import { requestAllPermissions } from './permissionService';

export const initializePushNotifications = async (userId, navigate) => {
    if (Capacitor.getPlatform() === 'web') {
        try {
            console.log("Initializing Web Push...");

            // 1. Request Permission & Get Token
            const permission = await Notification.requestPermission();
            if (permission !== 'granted') {
                console.warn("Web Notification permission denied");
                return;
            }

            // VAPID Key is public and safe to expose in client code
            // Using default registration first
            const token = await getToken(messaging).catch(async (err) => {
                console.log("Default getToken failed", err);
                return null;
            });

            if (token) {
                console.log('Web Push Token:', token);
                if (userId) {
                    const userRef = doc(db, 'usuarios', userId);
                    await updateDoc(userRef, { fcmTokens: arrayUnion(token) });
                }
            }

            // 2. Foreground Message Listener
            onMessage(messaging, (payload) => {
                console.log('Web Foreground Message:', payload);
                const { title, body } = payload.notification || {};

                if (title && body) {
                    window.dispatchEvent(new CustomEvent('kondo_inapp_notification', {
                        detail: {
                            title,
                            body,
                            data: payload.data || {}
                        }
                    }));
                }

                // Force show browser notification even if in foreground
                if (Notification.permission === 'granted') {
                    new Notification(title, { body });
                }
            });

        } catch (e) {
            console.error("Web Push Init Error:", e);
        }
        return;
    }

    // NATIVE PLATFORM (ANDROID/IOS)
    try {
        console.log("Native Push Initialization...");

        // 1. Ensure Permissions using centralized service
        await requestAllPermissions();

        // 2. Register for Token
        // Directly register. The plugin handles checks internally usually, or requestAllPermissions handled it.
        await PushNotifications.register();

        // 3. Create Channels
        await PushNotifications.createChannel({
            id: 'panic_channel',
            name: 'Alertas de Panico',
            description: 'Notificaciones criticas de seguridad',
            importance: 5,
            visibility: 1,
            sound: 'siren',
            vibration: true,
        });

        await PushNotifications.createChannel({
            id: 'general_channel_v3',
            name: 'Avisos Generales',
            importance: 4,
            visibility: 1,
            sound: 'kondo_notificacion',
            vibration: true,
        });

        // 4. listeners
        PushNotifications.removeAllListeners(); // Safety cleanup

        PushNotifications.addListener('registration', async (token) => {
            console.log('Push Registration Success. Token:', token.value);
            if (userId) {
                const userRef = doc(db, 'usuarios', userId);
                await updateDoc(userRef, { fcmTokens: arrayUnion(token.value) });
                console.log("FCM Token saved to user profile.");
            }
        });

        PushNotifications.addListener('registrationError', (error) => {
            console.error('Push Registration Error:', error);
        });

        PushNotifications.addListener('pushNotificationReceived', (notification) => {
            console.log('Push received:', notification);
            const pushData = notification.notification || notification;
            if (pushData && pushData.title && pushData.body) {
                // Despachar evento para Banner / Toast premium in-app en lugar del alert nativo:
                window.dispatchEvent(new CustomEvent('kondo_inapp_notification', {
                    detail: {
                        title: pushData.title,
                        body: pushData.body,
                        data: pushData.data || notification.data || {}
                    }
                }));
            }
        });

        PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
            // Robust data extraction (Native vs specific plugin versions)
            const data = notification.notification.data || notification.data || notification.notification;

            console.log('Action performed data:', data);

            if (data && navigate) {
                if (data.type === 'panic') {
                    const { userName, userUnit, timestamp } = data;
                    alert(`🚨 ALERTA DE SEGURIDAD 🚨\n\nVecino: ${userName}\nUnidad: ${userUnit}\nHora: ${timestamp}`);
                }

                // eslint-disable-next-line default-case
                switch (data.type) {
                    case 'chat': navigate(data.url); break;
                    case 'charge_created': navigate('/pagar-cuota'); break;
                    case 'announcement': navigate('/avisos'); break;
                    case 'wall_post': navigate('/muro'); break;
                    case 'access_entry': navigate('/accesos'); break;
                    case 'transparency': navigate('/finanzas'); break;
                    case 'report_update': navigate('/mis-reportes'); break;
                    case 'marketplace_update': navigate('/marketplace/mis-cotizaciones'); break;

                    // New Deep Links
                    case 'vehicle_approved': navigate('/vehiculos'); break;
                    case 'provider_approved': navigate('/proveedor/servicios'); break;
                    case 'reservation_reminder': navigate('/reservar'); break;

                    // Encuestas & Asambleas Links
                    case 'encuesta':
                    case 'encuesta_resultados':
                    case 'encuesta_reminder':
                        navigate('/encuestas');
                        break;
                    case 'asamblea':
                        navigate('/asambleas');
                        break;
                    case 'asamblea_en_vivo':
                        if (data.asambleaId) navigate(`/asambleas/${data.asambleaId}/sala`);
                        else navigate('/asambleas');
                        break;

                    // Licitaciones Links
                    case 'licitacion_nueva':
                        navigate('/licitaciones');
                        break;

                    // Paquetes y Estacionamientos
                    case 'paquete': navigate('/paqueteria'); break;
                    case 'estacionamiento': navigate('/estacionamientos'); break;

                    // Proveedores Generales (Solo Lectura para Residentes)
                    case 'proveedor_general': break;

                    // Marketplace Flow (Specific Routing)
                    case 'quote_request': navigate('/proveedor/cotizaciones'); break;
                    case 'quote_response_resident':
                        if (data.cotiId) navigate(`/marketplace/cotizacion/${data.cotiId}`);
                        else navigate('/marketplace/mis-cotizaciones');
                        break;
                    case 'quote_accepted_provider':
                        if (data.cotiId) navigate(`/proveedor/seguimiento/${data.cotiId}`);
                        else navigate('/proveedor/cotizaciones');
                        break;
                    case 'quote_rejected_provider': navigate('/proveedor/cotizaciones'); break;

                    case 'quote_reschedule_request':
                        if (data.cotiId) navigate(`/marketplace/cotizacion/${data.cotiId}`);
                        else navigate('/proveedor/cotizaciones');
                        break;

                    case 'service_en_route':
                        if (data.cotiId) navigate(`/marketplace/cotizacion/${data.cotiId}`);
                        break;
                    case 'service_arrived':
                        if (data.cotiId) navigate(`/marketplace/cotizacion/${data.cotiId}`);
                        break;
                    case 'payment_request':
                        if (data.cotiId) navigate(`/marketplace/cotizacion/${data.cotiId}`); // Resident confirms here
                        break;
                    case 'payment_confirmed':
                        if (data.cotiId) navigate(`/proveedor/seguimiento/${data.cotiId}`); // Provider resumes work
                        break;
                    case 'service_finished':
                        if (data.cotiId) navigate(`/marketplace/calificar/${data.cotiId}`);
                        break;

                    // Fallback
                    case 'quote_update': navigate('/marketplace'); break;
                }
            }
        });

    } catch (e) {
        console.error("Error initializing push notifications:", e);
    }
};
